import PhoneInput from 'react-phone-input-2';
import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Check, Calendar, Indent } from 'lucide-react';
import { User } from '../../utils/types';
import { VALIDATION_MESSAGES } from '../../utils/validationMessages';
import { UseFormReturn, Controller } from 'react-hook-form';
import SourceCombobox from '../common/SourceCombobox';
import {
  RegionalOption,
  fetchRegionalOptions,
} from '../../constants/countryRegionalOptions';

interface PersonalInformationSectionProps {
  register: UseFormReturn<Partial<User>>['register'];
  handleSubmit: UseFormReturn<Partial<User>>['handleSubmit'];
  setValue: UseFormReturn<Partial<User>>['setValue'];
  control: UseFormReturn<Partial<User>>['control'];
  isPersonalDirty: boolean;
  errors: UseFormReturn<Partial<User>>['formState']['errors'];
  onSubmitPersonal: (data: Partial<User>) => Promise<void>;
  profilePhone: string;
  setProfilePhone: (value: string) => void;
  user: User | null;
  setVerificationModal: (value: {
    isOpen: boolean;
    type: 'phone';
    value: string;
  }) => void;
}

const PersonalInformationSection = ({
  register,
  handleSubmit,
  setValue,
  control,
  isPersonalDirty,
  errors,
  onSubmitPersonal,
  profilePhone,
  setProfilePhone,
  user,
  setVerificationModal,
}: PersonalInformationSectionProps) => {
  const [countryOptions, setCountryOptions] = useState<RegionalOption[]>([]);
  const [isCountryLoading, setIsCountryLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadCountryOptions = async () => {
      try {
        setIsCountryLoading(true);
        const { countryOptions } = await fetchRegionalOptions({
          country: true,
        });

        if (!isMounted) return;

        setCountryOptions(countryOptions);
      } catch (error) {
        if (!isMounted) return;
        setCountryOptions([]);
      } finally {
        if (isMounted) {
          setIsCountryLoading(false);
        }
      }
    };

    loadCountryOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Indent className="h-5 w-5 text-primary" />
          Personal Information
        </CardTitle>
        <CardDescription>Update your personal details</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmitPersonal)} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-1">
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name</Label>
              <Input
                disabled
                id="fullName"
                {...register('fullName')}
                placeholder="Full Name"
                style={{ color: '#111827', opacity: 1 }}
              />
            </div>
          </div>

          <div className="flex flex-row gap-4">
            <div className="space-y-2 flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label htmlFor="email">Email Address</Label>
                </div>
              </div>
              <div className="relative">
                <Input
                  id="email"
                  type="email"
                  {...register('email')}
                  placeholder="Email address"
                  disabled
                  className={`${user?.emailVerified ? 'pr-10' : ''}`}
                  style={{ color: '#111827', opacity: 1 }}
                />
                {user?.emailVerified && (
                  <span
                    title="Verified"
                    className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex h-5 w-5 items-center justify-center rounded-full border border-green-200 bg-green-100 text-green-600 pointer-events-none"
                  >
                    <Check className="h-3 w-3" />
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-2 flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label htmlFor="phone">Phone Number</Label>
                </div>
                {!user?.phoneVerified && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs px-2"
                    onClick={() =>
                      setVerificationModal({
                        isOpen: true,
                        type: 'phone',
                        value: user?.phone || '',
                      })
                    }
                  >
                    Verify Phone
                  </Button>
                )}
              </div>
              <div className="relative">
                <PhoneInput
                  country={'in'}
                  value={profilePhone}
                  onChange={value => {
                    setProfilePhone(value);
                    setValue('phone', value, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }}
                  inputClass="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pl-16 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  buttonClass="absolute left-0 top-0 h-10 w-12 rounded-l-md border-r border-input bg-background hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  dropdownClass="bg-background border-border rounded-md shadow-lg mt-1"
                  searchClass="bg-background border-input rounded-md"
                  containerClass="w-full"
                  containerStyle={{
                    width: '100%',
                    height: '40px',
                  }}
                  preferredCountries={['in', 'us', 'gb', 'ca', 'au']}
                  enableSearch={true}
                  placeholder="Enter phone number"
                  inputStyle={{
                    width: '100%',
                    height: '40px',
                    lineHeight: '40px',
                    paddingLeft: '60px',
                    paddingRight: user?.phoneVerified ? '44px' : '12px',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '6px',
                    backgroundColor: 'hsl(var(--background))',
                    color: 'hsl(var(--foreground))',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                  buttonStyle={{
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '6px 0 0 6px',
                    backgroundColor: 'hsl(var(--background))',
                    color: 'hsl(var(--foreground))',
                    height: '40px',
                    width: '52px',
                  }}
                />
                <input
                  type="hidden"
                  {...register('phone', {
                    required: 'Phone number is required',
                    validate: value => {
                      if (!value || value.length < 10) {
                        return VALIDATION_MESSAGES.phone.minLength;
                      }
                      if (value.length > 15) {
                        return VALIDATION_MESSAGES.phone.maxLength;
                      }
                      return true;
                    },
                  })}
                />
                {user?.phoneVerified && (
                  <span
                    title="Verified"
                    className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex h-5 w-5 items-center justify-center rounded-full border border-green-200 bg-green-100 text-green-600 pointer-events-none"
                  >
                    <Check className="h-3 w-3" />
                  </span>
                )}
              </div>
              {errors.phone && (
                <p className="text-sm text-destructive">
                  {String(errors.phone.message)}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Input
              id="address"
              {...register('address')}
              placeholder="Street address"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input id="city" {...register('city')} placeholder="City" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="state">State</Label>
              <Input id="state" {...register('state')} placeholder="State" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Controller
                name="country"
                control={control}
                render={({ field }) => (
                  <SourceCombobox
                    id="country"
                    items={countryOptions.map(option => option.label)}
                    placeholder="Select country"
                    value={
                      countryOptions.find(
                        option => option.value === field.value
                      )?.label || ''
                    }
                    onChange={value => {
                      const country = countryOptions.find(
                        option => option.label === value
                      );
                      if (country) {
                        field.onChange(country.value);
                      }
                    }}
                    disabled={isCountryLoading}
                    emptyText="No countries found."
                  />
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dob">Date of Birth</Label>
              <div className="relative">
                <Input
                  id="dob"
                  disabled
                  type="date"
                  {...register('dob')}
                  style={{ color: '#111827', opacity: 1 }}
                  className="h-10 w-full pr-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                />
                <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={!isPersonalDirty}
              className="w-full sm:w-auto"
            >
              Save Changes
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default PersonalInformationSection;
